import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, ArrowUp, ArrowDown, Pencil, Trash2 } from "lucide-react";

const empty = { name: "", min_score: "", max_score: "", description: "", active: true };

export function GradingScaleTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/grading-scale").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  async function save() {
    if (!form.name) { toast.error("Grade name is required"); return; }
    const payload = {
      name: form.name,
      min_score: parseFloat(form.min_score),
      max_score: parseFloat(form.max_score),
      description: form.description || null,
      active: form.active,
    };
    if (Number.isNaN(payload.min_score) || Number.isNaN(payload.max_score)) { toast.error("Enter valid minimum and maximum scores"); return; }
    try {
      if (editing) await api.patch(`/grading-scale/${editing}`, payload);
      else await api.post("/grading-scale", payload);
      toast.success("Grading scale saved");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function toggle(g) {
    try { await api.patch(`/grading-scale/${g.id}`, { active: !g.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function remove(g) {
    if (!window.confirm(`Delete grade ${g.name}?`)) return;
    try { await api.delete(`/grading-scale/${g.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  async function move(idx, dir) {
    const next = [...list];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setList(next);
    try { await api.post("/grading-scale/reorder", { ids: next.map((g) => g.id) }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600 max-w-xl">
          Grades are stored in the database and applied automatically to exam and homework scores.
          Only active grades are used; score ranges may not overlap.
        </p>
        <Button data-testid="grade-add" className="rounded-full bg-[#0F1E4F] text-white"
          onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}>
          <Plus className="h-4 w-4 mr-1" /> Add grade
        </Button>
      </div>

      <Card data-testid="grading-scale-table" className="rounded-2xl border-slate-200 overflow-hidden">
        {list.length === 0 ? <div className="p-10 text-center text-slate-500 text-sm">No grades defined yet.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Order</TableHead><TableHead>Grade</TableHead><TableHead>Min</TableHead>
              <TableHead>Max</TableHead><TableHead>Description</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((g, i) => (
                <TableRow key={g.id} data-testid={`grade-row-${g.name}`}>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" data-testid={`grade-up-${g.name}`} className="h-7 w-7 p-0 rounded-full" onClick={() => move(i, -1)}><ArrowUp className="h-3 w-3" /></Button>
                      <Button size="sm" variant="outline" data-testid={`grade-down-${g.name}`} className="h-7 w-7 p-0 rounded-full" onClick={() => move(i, 1)}><ArrowDown className="h-3 w-3" /></Button>
                    </div>
                  </TableCell>
                  <TableCell className="font-serif font-black text-[#0F1E4F]">{g.name}</TableCell>
                  <TableCell>{g.min_score}</TableCell>
                  <TableCell>{g.max_score}</TableCell>
                  <TableCell className="text-slate-600">{g.description || "—"}</TableCell>
                  <TableCell>
                    <button data-testid={`grade-toggle-${g.name}`} onClick={() => toggle(g)}>
                      <Badge className={`rounded-full border-0 ${g.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                        {g.active ? "Active" : "Inactive"}
                      </Badge>
                    </button>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1.5 justify-end">
                      <Button size="sm" variant="outline" data-testid={`grade-edit-${g.name}`} className="h-8 rounded-full"
                        onClick={() => { setEditing(g.id); setForm({ name: g.name, min_score: g.min_score, max_score: g.max_score, description: g.description || "", active: g.active }); setOpen(true); }}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`grade-delete-${g.name}`} className="h-8 rounded-full border-red-200 text-red-700" onClick={() => remove(g)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit grade" : "New grade"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Grade name</Label>
              <Input data-testid="grade-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Minimum score</Label>
                <Input data-testid="grade-min" type="number" value={form.min_score} onChange={(e) => setForm({ ...form, min_score: e.target.value })} className="h-11 rounded-xl mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Maximum score</Label>
                <Input data-testid="grade-max" type="number" value={form.max_score} onChange={(e) => setForm({ ...form, max_score: e.target.value })} className="h-11 rounded-xl mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Description (optional)</Label>
              <Textarea rows={2} data-testid="grade-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="grade-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              Active (used for automatic grading)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="grade-save" className="rounded-full bg-[#0F1E4F] text-white" onClick={save}>Save grade</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
